import React, { useContext, useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../UserContext.js";
import { progressService } from "../../../../services/progressService.js";
import spaceGrowingIllustration from "../../../../assets/spaceGrowingIllustration.png";
// import { IoMdChatboxes } from "react-icons/io";
import { FaArrowRight } from "react-icons/fa6";
import greenLeaf from "../../../../assets/greenLeaf.png";
import yellowLeaf from "../../../../assets/yellowLeaf.png";
import silverLeaf from "../../../../assets/silverLeaf.png";
import { apiFetch } from "../../../../services/apiFetch.js";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

const affirmations = [
  {
    text: "You have the right to perform your prescribed duty, but you are not entitled to the fruits of action",
    source: "Bhagavad Gita",
  },
  {
    text: "Success is when your signature changes to an autograph.",
    source: "Dr. A.P.J. Abdul Kalam",
  },
  {
    text: "You cannot cross the sea merely by standing and staring at the water.",
    source: "Rabindranath Tagore",
  },
  {
    text: "Management is doing things right; leadership is doing the right things.",
    source: "Peter Drucker",
  },
  {
    text: "Luck is what happens when preparation meets opportunity.",
    source: "Seneca",
  },
  {
    text: "Everything you've ever wanted is on the other side of fear.",
    source: "George Addair",
  },
  {
    text: "Don't find fault, find a remedy.",
    source: "Henry Ford",
  },
  {
    text: "Dress for the job you want, not the job you have.",
    source: "Unknown",
  },
  {
    text: "If you want to go fast, go alone. If you want to go far, go together.",
    source: "African Proverb",
  },
  {
    text: "The best way to predict the future is to create it.",
    source: "Abraham Lincoln",
  },
  {
    text: "Action is better than inaction.",
    source: "Bhagavad Gita",
  },
  {
    text: "You do not rise to the level of your goals. You fall to the level of your systems.",
    source: "James Clear",
  },
  {
    text: "People don't buy what you do; they buy why you do it.",
    source: "Simon Sinek",
  },
  {
    text: "We suffer more often in imagination than in reality.",
    source: "Seneca",
  },
  {
    text: "You must do the thing you think you cannot do.",
    source: "Eleanor Roosevelt",
  },
  {
    text: "You miss 100% of the shots you don't take.",
    source: "Wayne Gretzky",
  },
  {
    text: "Success usually comes to those who are too busy to be looking for it.",
    source: "Henry David Thoreau",
  },
  {
    text: "A man who knows everything is a fool.",
    source: "Chankya",
  },
  {
    text: "What stands in the way becomes the way.",
    source: "Marcus Aurelius",
  },
  {
    text: "Waste no more time arguing about what a good man should be. Be one",
    source: "Marcus Aurelius",
  },
  {
    text: "Reject your sense of injury and the injury itself disappears.",
    source: "Marcus Aurelius",
  },
];

// Helper function to unwrap nested payload
const unwrapPayload = (node) => {
  let current = node;
  let depth = 0;
  const maxDepth = 10;

  while (
    current &&
    typeof current === "object" &&
    (current.payload || current.data) &&
    depth < maxDepth
  ) {
    current = current.payload || current.data;
    depth++;
  }
  
  return current;
};

const DashboardWelcomeSection = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const userName = user?.firstName ? `${user.firstName}` : "";
  const userId = user?.user_id;
  const token = user?.access_token;

  // State for backend data
  const [courseProgress, setCourseProgress] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [supportItem, setSupportItem] = useState(null);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState(null);
  const [showJourneyLockedModal, setShowJourneyLockedModal] = useState(false);

  // Random quote on every mounting
  const dailyAffirmation = useMemo(() => {
    return affirmations[Math.floor(Math.random() * affirmations.length)];
  }, []);

  // Fetch courses and select the first one
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await apiFetch(`${apiBaseURL}/courses`);
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          setSelectedCourse(data[0]); // Select first course
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
      }
    };

    if (userId) {
      fetchCourses();
    }
  }, [userId]);

  // Fetch course progress
  useEffect(() => {
    const fetchCourseProgress = async () => {
      if (!userId || !selectedCourse) return;

      try {
        setLoading(true);
        const response = await apiFetch(
          `${apiBaseURL}/users/${userId}/courses/${selectedCourse.id}/progress`
        );
        const data = await response.json();
        setCourseProgress(data);
      } catch (error) {
        console.error("Error fetching course progress:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourseProgress();
  }, [userId, selectedCourse]);

  // Fetch profile image
  useEffect(() => {
    const fetchProfileImage = async () => {
      if (!userId) return;

      try {
        const res = await progressService.getUserDataForSection(
          userId,
          "course_career_progression",
          "identity",
          "current_identity_reflection"
        );

        let payload = null;
        
        if (res?.data?.payload) {
          payload = unwrapPayload(res.data.payload);
        } else if (res?.payload) {
          payload = unwrapPayload(res.payload);
        } else if (res?.data?.data) {
          payload = unwrapPayload(res.data.data);
        } else if (res?.data) {
          payload = unwrapPayload(res.data);
        } else {
          payload = unwrapPayload(res);
        }

        if (payload && typeof payload === 'object') {
          const image = payload.profileImage || payload.image || payload.avatar || null;
          setProfileImage(image);
        }
      } catch (error) {
        console.error("Error fetching profile image:", error);
      }
    };

    fetchProfileImage();
  }, [userId]);

  // Fetch coach + AP for the currently selected course
  useEffect(() => {
    if (!userId || !selectedCourse?.id) return;
    if (!token) {
      setSupportItem(null);
      setSupportError("Missing access token");
      return;
    }

    const controller = new AbortController();

    const fetchSupport = async () => {
      try {
        setSupportLoading(true);
        setSupportError(null);

        const params = new URLSearchParams();
        params.set("course_id", selectedCourse.id);
        params.set("active_only", "true");

        const res = await fetch(
          `${apiBaseURL}/users/${userId}/support?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          }
        );

        if (!res.ok) {
          throw new Error(`Failed to load growth partners (${res.status})`);
        }

        const data = await res.json();
        const items = Array.isArray(data?.support) ? data.support : [];
        const matched =
          items.find((s) => String(s?.course?.id) === String(selectedCourse.id)) ||
          items[0] ||
          null;
        setSupportItem(matched);
      } catch (err) {
        if (err?.name === "AbortError") return;
        console.error("Error fetching support:", err);
        setSupportItem(null);
        setSupportError(err?.message || "Failed to load growth partners");
      } finally {
        setSupportLoading(false);
      }
    };

    fetchSupport();

    return () => controller.abort();
  }, [userId, selectedCourse?.id, token]);

  // Calculate module stats
  const moduleStats = useMemo(() => {
    if (!courseProgress?.modules) {
      return {
        completed: 0,
        inProgress: 0,
        pending: 0,
        total: 0,
        activeModuleIndex: 0,
      };
    }


    const modules = courseProgress.modules;
    const total = modules.length;    
    let completed = 0;
    let inProgress = 0;
    let pending = 0;
    let activeModuleIndex = 0;

    modules.forEach((module, index) => {
      const allSubsCompleted = module.subsections?.every(
        (sub) => sub.completed || sub.status === "completed"
      );
      const someSubsCompleted = module.subsections?.some(
        (sub) => sub.completed || sub.status === "completed"
      );

      if (allSubsCompleted) {
        completed++;
      } else if (someSubsCompleted) {
        inProgress++;
        if (activeModuleIndex === 0 || activeModuleIndex === completed) {
          activeModuleIndex = index;
        }
      } else {
        pending++;
      }
    });

    // If no active module found, set to first incomplete
    if (activeModuleIndex === 0 && completed > 0) {
      activeModuleIndex = completed;
    }

    return {
      completed,
      inProgress,
      pending,
      total,
      activeModuleIndex,
    };
  }, [courseProgress]);

  const isModuleCompletedByOrder = useCallback(
    (order) => {
      const modules = courseProgress?.modules || [];
      const moduleItem = modules.find((m) => m?.order === order);
      if (!moduleItem?.subsections?.length) return false;
      return moduleItem.subsections.every(
        (sub) => sub?.completed === true || sub?.status === "completed"
      );
    },
    [courseProgress]
  );

  const canAccessJourney = useMemo(
    () => [1, 2, 3, 4].every((order) => isModuleCompletedByOrder(order)),
    [isModuleCompletedByOrder]
  );

  const handleJourneyClick = () => {
    if (canAccessJourney) {
      navigate("/learner/journey");
      return;
    }
    setShowJourneyLockedModal(true);
  };

  const coach = supportItem?.coach || null;
  const ap = supportItem?.accountability_partner || null;
  const coachName =
    coach && (coach.first_name || coach.last_name || coach.email)
      ? `${coach.first_name || ""} ${coach.last_name || ""}`.trim() || coach.email
      : "Not assigned yet";
  const apName =
    ap && (ap.first_name || ap.last_name || ap.email)
      ? `${ap.first_name || ""} ${ap.last_name || ""}`.trim() || ap.email
      : "Not assigned yet";

  return (
    <section className="w-full bg-white px-4 sm:px-6 lg:px-10 py-8 font-gotham">
      {/* Top Welcome Section */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
        {/* Left Text */}
        <div className="max-w-2xl">
          <h1 className="text-3xl sm:text-4xl font-medium font-gotham text-[#000000]">
            Welcome to Your Space, {userName}!
          </h1>
          <p className="mt-3 text-[#000000] font-gotham font-normal text-base sm:text-lg">
            This is your space to reflect, track, and visualise grow.
            Everything that matters to your career journey, in one place.
          </p>
        </div>

        {/* Right Illustration Placeholder */}
        <div className="w-full lg:w-[140px] h-[100px] bg-gray-100 rounded-xl flex items-center justify-center">
          <img
            src={spaceGrowingIllustration}
            alt="Space Growing Illustration"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Main Grid */}
      <div className="max-w-7xl mx-auto mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="w-full max-w-xl rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          {/* Top Gradient */}
          {/* <div
            className="relative pt-6 pb-14 text-center"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(0, 95, 94, 0.45) 0%, rgba(3, 74, 69, 0.25) 35%, rgba(255,255,255,1) 70%)",
            }}
          > */}
          <div
            className="relative w-full pt-10 pb-16 text-center overflow-hidden rounded-t-3xl"
            style={{
              background:
                "radial-gradient(ellipse at top, rgba(180, 232, 231, 0.9) 0%, rgba(215, 245, 244, 0.7) 35%, rgba(255,255,255,1) 70%)",
            }}
          >
            <h2 className="text-xl md:text-2xl font-medium font-gotham text-[#1D1D1D]">
              Your Promotion Journey
            </h2>

            {/* Waves */}
            <div className="absolute left-6 top-20 opacity-40">
              <svg width="36" height="8" viewBox="0 0 36 8" fill="none">
                <path
                  d="M0 4C4 0 8 8 12 4C16 0 20 8 24 4C28 0 32 8 36 4"
                  stroke="#E4E4E4"
                  strokeWidth="1.5"
                  fill="none"
                />
              </svg>
            </div>

            <div className="absolute right-6 top-20 opacity-40">
              <svg width="36" height="8" viewBox="0 0 36 8" fill="none">
                <path
                  d="M0 4C4 0 8 8 12 4C16 0 20 8 24 4C28 0 32 8 36 4"
                  stroke="#E4E4E4"
                  strokeWidth="1.5"
                  fill="none"
                />
              </svg>
            </div>

            {/* Avatar with Profile Image */}
            <div className="mt-6 flex justify-center">
              <div className="w-20 h-20 md:h-[113.03515625px] md:w-[113.03515625px] rounded-full bg-white flex items-center justify-center shadow">
                <div className="w-24 h-24 md:h-[97.1584243774414px] md:w-[97.1584243774414px] rounded-full bg-[#F8F8F8] flex items-center justify-center text-xs text-[#1D1D1D] overflow-hidden">
                  {profileImage ? (
                    <img
                      src={profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Inner Card */}
          <div className="relative -mt-10 mx-4 md:mx-6 bg-white rounded-2xl border border-[#E3E3E3] overflow-hidden">
            {/* Track Header */}
            <div className="bg-[#208887] py-3 text-center cursor-pointer" onClick={() => navigate("/learner/learning")}>
              <p className="text-sm font-semibold font-gotham tracking-widest text-white underline">
                {/* CAREER PROGRESSION TRACK */}
                YOUR LEARNING PATH
              </p>
            </div>

            {/* Modules */}
            <div className="px-6 pt-6">
              {loading ? (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
              ) : (
          
                <div className="flex justify-between">
                  {courseProgress?.modules?.map((module, index) => {
                    const allSubsCompleted = module.subsections?.every(
                      (sub) => sub.completed || sub.status === "completed"
                    );
                    const someSubsCompleted = module.subsections?.some(
                      (sub) => sub.completed || sub.status === "completed"
                    );

                    const isCompleted = allSubsCompleted;
                    const isInProgress = !allSubsCompleted && someSubsCompleted;

                    let leaf;
                    if (isCompleted) leaf = greenLeaf;
                    else if (isInProgress) leaf = yellowLeaf;
                    else leaf = silverLeaf;

                    return (
                      <div key={module.id} className="flex flex-col items-center">
                        <div className="relative w-12 h-12">
                          <img
                            src={leaf}
                            alt="module-status"
                            className="w-full h-full object-contain"
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-[#1D1D1D] bottom-2.5">
                            M{index + 1}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 text-center px-6 py-8 divide-x divide-gray-200 font-gotham">
              <div>
                <p className="text-4xl font-medium text-[#323232]">
                  {loading ? (
                    <span className="inline-block w-12 h-10 bg-gray-200 rounded animate-pulse"></span>
                  ) : (
                    String(moduleStats.completed).padStart(2, "0")
                  )}
                </p>
                <p className="mt-2 text-sm text-[#000000] font-gotham font-normal">
                  modules completed
                </p>
              </div>

              <div>
                <p className="text-4xl font-medium text-[#323232]">
                  {loading ? (
                    <span className="inline-block w-12 h-10 bg-gray-200 rounded animate-pulse"></span>
                  ) : (
                    String(moduleStats.inProgress).padStart(2, "0")
                  )}
                </p>
                <p className="mt-2 text-sm text-[#000000] font-gotham font-normal">
                  module in-progress
                </p>
              </div>

              <div>
                <p className="text-4xl font-medium text-[#323232]">
                  {loading ? (
                    <span className="inline-block w-12 h-10 bg-gray-200 rounded animate-pulse"></span>
                  ) : (
                    String(moduleStats.pending).padStart(2, "0")
                  )}
                </p>
                <p className="mt-2 text-sm text-[#000000] font-gotham font-normal">
                  modules pending
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handleJourneyClick}
            className="mt-6 w-full bg-black text-white py-5 px-6 flex items-center justify-between text-base font-gotham md:text-lg font-medium hover:bg-gray-800 transition-colors"
          >
            <span>See Your Journey</span>
            <span className="text-xl">
              <FaArrowRight className="text-white" />
            </span>
          </button>
        </div>

        

        {/* Right Column */}
        <div className="flex flex-col gap-6">
          {/* Growth Partners */}
          {/* <div className="rounded-2xl border border-[#E6E6E6] bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg md:text-xl font-medium text-[#1D1D1D]">
                Your Growth Partners
              </h3>
              <div className="flex-1 ml-4 h-px bg-[#E6E6E6]" />
            </div>

            {supportError && (
              <p className="text-xs text-red-600 mb-4">{supportError}</p>
            )}

            <div className="flex flex-col sm:flex-row gap-10">
              {[
                {
                  key: "coach",
                  name: supportLoading ? "Loading..." : coachName,
                  role: "CAREER COACH",
                },
                {
                  key: "ap",
                  name: supportLoading ? "Loading..." : apName,
                  role: "ACCOUNTABILITY PARTNER",
                },
              ].map((p, i) => (
                <div key={p.key || i} className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-[#DFF1EF] flex items-center justify-center overflow-hidden">
                    <span className="text-sm text-gray-700 font-medium">
                      {p.name?.[0]?.toUpperCase() || "?"}
                    </span>
                  </div>

                  <div className="leading-tight">
                    <div className="flex gap-1">
                      <p className="font-medium font-gotham text-xl md:text-lg text-[#1C1C1C]">
                        {p.name}
                      </p>
                      <span className="">
                        <IoMdChatboxes className="text-[#54B0AF] text-xl mt-1" />
                      </span>
                    </div>
                    <p className="text-xs md:text-[10px] text-[#AAAAAA] font-medium font-gotham uppercase tracking-wide">
                      {p.role}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div> */}
          <div className="rounded-2xl border border-[#E6E6E6] bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg md:text-xl font-medium text-[#1D1D1D]">
                Your Growth Partners
              </h3>
              <div className="flex-1 ml-4 h-px bg-[#E6E6E6]" />
            </div>

            {supportError && (
              <p className="text-xs text-red-600 mb-4">{supportError}</p>
            )}

            {/* Equal width container */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              {[
                {
                  key: "coach",
                  name: supportLoading ? "Loading..." : coachName,
                  role: "CAREER COACH",
                },
                {
                  key: "ap",
                  name: supportLoading ? "Loading..." : apName,
                  role: "ACCOUNTABILITY PARTNER",
                },
              ].map((p, i) => (
                <div
                  key={p.key || i}
                  className="flex items-center gap-4 w-full"
                >
                  {/* Avatar */}
                  <div className="w-14 h-14 rounded-full bg-[#DFF1EF] flex items-center justify-center overflow-hidden flex-shrink-0">
                    <span className="text-sm text-gray-700 font-medium">
                      {p.name?.[0]?.toUpperCase() || "?"}
                    </span>
                  </div>

                  {/* Text */}
                  <div className="leading-tight flex-1">
                    <div className="flex items-center gap-1">
                      <p className="font-medium font-gotham text-lg md:text-xl text-[#1C1C1C] truncate">
                        {/* {p.name} */}
                        {p.name && p.name.length > 13
                          ? `${p.name.slice(0, 13)}...`
                          : p.name}
                      </p>
                      {/* <IoMdChatboxes className="text-[#54B0AF] text-xl mt-[2px]" /> */}
                    </div>

                    <p className="text-xs md:text-[10px] text-[#AAAAAA] font-medium font-gotham uppercase tracking-wide">
                      {p.role}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Daily Affirmation */}
          <div className="relative rounded-2xl min-h-[360px] border border-[#E6E6E6] bg-white p-8 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between mb-10 relative z-10">
              <h3 className="text-lg md:text-xl font-medium font-gotham text-[#1D1D1D]">
                Your Daily Affirmation
              </h3>
              <div className="flex-1 ml-4 h-px bg-[#E6E6E6]" />
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center gap-6 text-center">
              <div className="text-5xl leading-none text-gray-700">"</div>

              <p className="text-xl sm:text-2xl text-[#1D1D1D] font-gotham font-[300] leading-relaxed max-w-md">
                {dailyAffirmation.text}
              </p>

              {/* <p className="mt-4 text-sm italic text-[#1D1D1D] font-gotham font-normal absolute -bottom-3">
                {dailyAffirmation.source}
              </p> */}
            </div>
            <p className="text-sm italic text-center w-full text-[#1D1D1D] font-gotham font-normal absolute bottom-5 flex justify-center">
              {dailyAffirmation.source}
            </p>

            {/* Bottom teal circular glow */}
            <div
              className="absolute left-1/2 -translate-x-1/2 bottom-[-45%]
                        w-[65%] aspect-square rounded-full blur-[3px]"
              style={{
                background:
                  "radial-gradient(circle, rgba(28,239,204,0.26) 0%, rgba(12,184,181,0.16) 40%, rgba(49,236,208,0) 68%)",
              }}
            />
          </div>
        </div>
      </div>

      {showJourneyLockedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-end">
              <button
                onClick={() => setShowJourneyLockedModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="text-center mt-4">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-[#54B0AF] bg-opacity-10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-[#54B0AF]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-medium font-gotham text-[#1D1D1D] mb-3">
                Complete Modules 1-4 to Unlock
              </h2>

              <p className="text-base text-gray-600 font-gotham mb-6">
                Complete modules 1 to 4 of your learning path to unlock "My Journey".
              </p>

              <button
                onClick={() => {
                  setShowJourneyLockedModal(false);
                  navigate("/learner/learning");
                }}
                className="w-full bg-black text-white py-3 px-6 rounded-lg font-medium font-gotham hover:bg-gray-800 transition-colors"
              >
                Go to Learning Path
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DashboardWelcomeSection;
// import React, { useContext, useMemo, useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom";
// import { UserContext } from "../../../../UserContext.js";
// import { progressService } from "../../../../services/progressService.js";
// import spaceGrowingIllustration from "../../../../assets/spaceGrowingIllustration.png";
// // import { IoMdChatboxes } from "react-icons/io";
// import { FaArrowRight } from "react-icons/fa6";
// import greenLeaf from "../../../../assets/greenLeaf.png";
// import yellowLeaf from "../../../../assets/yellowLeaf.png";
// import silverLeaf from "../../../../assets/silverLeaf.png";

// const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

// const affirmations = [
//   {
//     text: "You have the right to perform your prescribed duty, but you are not entitled to the fruits of action",
//     source: "Bhagavad Gita",
//   },
//   {
//     text: "Success is when your signature changes to an autograph.",
//     source: "Dr. A.P.J. Abdul Kalam",
//   },
//   {
//     text: "You cannot cross the sea merely by standing and staring at the water.",
//     source: "Rabindranath Tagore",
//   },
//   {
//     text: "Management is doing things right; leadership is doing the right things.",
//     source: "Peter Drucker",
//   },
//   {
//     text: "Luck is what happens when preparation meets opportunity.",
//     source: "Seneca",
//   },
//   {
//     text: "Everything you’ve ever wanted is on the other side of fear.",
//     source: "George Addair",
//   },
//   {
//     text: "Don't find fault, find a remedy.",
//     source: "Henry Ford",
//   },
//   {
//     text: "Dress for the job you want, not the job you have.",
//     source: "Unknown",
//   },
//   {
//     text: "If you want to go fast, go alone. If you want to go far, go together.",
//     source: "African Proverb",
//   },
//   {
//     text: "The best way to predict the future is to create it.",
//     source: "Abraham Lincoln",
//   },
//     {
//     text: "Action is better than inaction.",
//     source: "Bhagavad Gita",
//   },
//   {
//     text: "You do not rise to the level of your goals. You fall to the level of your systems.",
//     source: "James Clear",
//   },
//     {
//     text: "People don’t buy what you do; they buy why you do it.",
//     source: "Simon Sinek",
//   },
//   {
//     text: "We suffer more often in imagination than in reality.",
//     source: "Seneca",
//   },
//     {
//     text: "You must do the thing you think you cannot do.",
//     source: "Eleanor Roosevelt",
//   },
//   {
//     text: "You miss 100% of the shots you don’t take.",
//     source: "Wayne Gretzky",
//   },
//     {
//     text: "Success usually comes to those who are too busy to be looking for it.",
//     source: "Henry David Thoreau",
//   },
//   {
//     text: "A man who knows everything is a fool.",
//     source: "Chankya",
//   },
//     {
//     text: "What stands in the way becomes the way.",
//     source: "Marcus Aurelius",
//   },
//   {
//     text: "Waste no more time arguing about what a good man should be. Be one",
//     source: "Marcus Aurelius",
//   },
//     {
//     text: "Reject your sense of injury and the injury itself disappears.",
//     source: "Marcus Aurelius",
//   },
// ];

// // Helper function to unwrap nested payload
// const unwrapPayload = (node) => {
//   let current = node;
//   let depth = 0;
//   const maxDepth = 10;

//   while (
//     current &&
//     typeof current === "object" &&
//     (current.payload || current.data) &&
//     depth < maxDepth
//   ) {
//     current = current.payload || current.data;
//     depth++;
//   }
  
//   return current;
// };

// const DashboardWelcomeSection = () => {
//   const { user } = useContext(UserContext);
//   const navigate = useNavigate();
//   const userName = user?.firstName ? `${user.firstName}` : "";
//   const userId = user?.user_id;
//   const token = user?.access_token;

//   // State for backend data
//   const [courseProgress, setCourseProgress] = useState(null);
//   const [profileImage, setProfileImage] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [selectedCourse, setSelectedCourse] = useState(null);
//   const [supportItem, setSupportItem] = useState(null);
//   const [supportLoading, setSupportLoading] = useState(false);
//   const [supportError, setSupportError] = useState(null);

//   // Random quote on every mounting
//   const dailyAffirmation = useMemo(() => {
//     return affirmations[Math.floor(Math.random() * affirmations.length)];
//   }, []);

//   // Fetch courses and select the first one
//   useEffect(() => {
//     const fetchCourses = async () => {
//       try {
//         const response = await fetch(`${apiBaseURL}/courses`);
//         const data = await response.json();
//         if (Array.isArray(data) && data.length > 0) {
//           setSelectedCourse(data[0]); // Select first course
//         }
//       } catch (error) {
//         console.error("Error fetching courses:", error);
//       }
//     };

//     if (userId) {
//       fetchCourses();
//     }
//   }, [userId]);

//   // Fetch course progress
//   useEffect(() => {
//     const fetchCourseProgress = async () => {
//       if (!userId || !selectedCourse) return;

//       try {
//         setLoading(true);
//         const response = await fetch(
//           `${apiBaseURL}/users/${userId}/courses/${selectedCourse.id}/progress`
//         );
//         const data = await response.json();
//         setCourseProgress(data);
//       } catch (error) {
//         console.error("Error fetching course progress:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchCourseProgress();
//   }, [userId, selectedCourse]);

//   // Fetch profile image
//   useEffect(() => {
//     const fetchProfileImage = async () => {
//       if (!userId) return;

//       try {
//         const res = await progressService.getUserDataForSection(
//           userId,
//           "course_career_progression",
//           "identity",
//           "current_identity_reflection"
//         );

//         let payload = null;
        
//         if (res?.data?.payload) {
//           payload = unwrapPayload(res.data.payload);
//         } else if (res?.payload) {
//           payload = unwrapPayload(res.payload);
//         } else if (res?.data?.data) {
//           payload = unwrapPayload(res.data.data);
//         } else if (res?.data) {
//           payload = unwrapPayload(res.data);
//         } else {
//           payload = unwrapPayload(res);
//         }

//         if (payload && typeof payload === 'object') {
//           const image = payload.profileImage || payload.image || payload.avatar || null;
//           setProfileImage(image);
//         }
//       } catch (error) {
//         console.error("Error fetching profile image:", error);
//       }
//     };

//     fetchProfileImage();
//   }, [userId]);

//   // Fetch coach + AP for the currently selected course
//   useEffect(() => {
//     if (!userId || !selectedCourse?.id) return;
//     if (!token) {
//       setSupportItem(null);
//       setSupportError("Missing access token");
//       return;
//     }

//     const controller = new AbortController();

//     const fetchSupport = async () => {
//       try {
//         setSupportLoading(true);
//         setSupportError(null);

//         const params = new URLSearchParams();
//         params.set("course_id", selectedCourse.id);
//         params.set("active_only", "true");

//         const res = await fetch(
//           `${apiBaseURL}/users/${userId}/support?${params.toString()}`,
//           {
//             method: "GET",
//             headers: {
//               "Content-Type": "application/json",
//               Authorization: `Bearer ${token}`,
//             },
//             signal: controller.signal,
//           }
//         );

//         if (!res.ok) {
//           throw new Error(`Failed to load growth partners (${res.status})`);
//         }

//         const data = await res.json();
//         const items = Array.isArray(data?.support) ? data.support : [];
//         const matched =
//           items.find((s) => String(s?.course?.id) === String(selectedCourse.id)) ||
//           items[0] ||
//           null;
//         setSupportItem(matched);
//       } catch (err) {
//         if (err?.name === "AbortError") return;
//         console.error("Error fetching support:", err);
//         setSupportItem(null);
//         setSupportError(err?.message || "Failed to load growth partners");
//       } finally {
//         setSupportLoading(false);
//       }
//     };

//     fetchSupport();

//     return () => controller.abort();
//   }, [userId, selectedCourse?.id, token]);

//   // Calculate module stats
//   const moduleStats = useMemo(() => {
//     if (!courseProgress?.modules) {
//       return {
//         completed: 0,
//         inProgress: 0,
//         pending: 0,
//         total: 0,
//         activeModuleIndex: 0,
//       };
//     }


//     const modules = courseProgress.modules;
//     const total = modules.length;    
//     let completed = 0;
//     let inProgress = 0;
//     let pending = 0;
//     let activeModuleIndex = 0;

//     modules.forEach((module, index) => {
//       const allSubsCompleted = module.subsections?.every(
//         (sub) => sub.completed || sub.status === "completed"
//       );
//       const someSubsCompleted = module.subsections?.some(
//         (sub) => sub.completed || sub.status === "completed"
//       );

//       if (allSubsCompleted) {
//         completed++;
//       } else if (someSubsCompleted) {
//         inProgress++;
//         if (activeModuleIndex === 0 || activeModuleIndex === completed) {
//           activeModuleIndex = index;
//         }
//       } else {
//         pending++;
//       }
//     });

//     // If no active module found, set to first incomplete
//     if (activeModuleIndex === 0 && completed > 0) {
//       activeModuleIndex = completed;
//     }

//     return {
//       completed,
//       inProgress,
//       pending,
//       total,
//       activeModuleIndex,
//     };
//   }, [courseProgress]);

//   const coach = supportItem?.coach || null;
//   const ap = supportItem?.accountability_partner || null;
//   const coachName =
//     coach && (coach.first_name || coach.last_name || coach.email)
//       ? `${coach.first_name || ""} ${coach.last_name || ""}`.trim() || coach.email
//       : "Not assigned yet";
//   const apName =
//     ap && (ap.first_name || ap.last_name || ap.email)
//       ? `${ap.first_name || ""} ${ap.last_name || ""}`.trim() || ap.email
//       : "Not assigned yet";

//   return (
//     <section className="w-full bg-white px-4 sm:px-6 lg:px-10 py-8 font-gotham">
//       {/* Top Welcome Section */}
//       <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
//         {/* Left Text */}
//         <div className="max-w-2xl">
//           <h1 className="text-3xl sm:text-4xl font-medium font-gotham text-[#000000]">
//             Welcome to Your Space, {userName}!
//           </h1>
//           <p className="mt-3 text-[#000000] font-gotham font-normal text-base sm:text-lg">
//             This is your space to reflect, track, and visualise grow.
//             Everything that matters to your career journey, in one place.
//           </p>
//         </div>

//         {/* Right Illustration Placeholder */}
//         <div className="w-full lg:w-[140px] h-[100px] bg-gray-100 rounded-xl flex items-center justify-center">
//           <img
//             src={spaceGrowingIllustration}
//             alt="Space Growing Illustration"
//             className="w-full h-full object-contain"
//           />
//         </div>
//       </div>

//       {/* Main Grid */}
//       <div className="max-w-7xl mx-auto mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
//         <div className="w-full max-w-xl rounded-3xl border border-gray-200 bg-white shadow-sm overflow-hidden">
//           {/* Top Gradient */}
//           {/* <div
//             className="relative pt-6 pb-14 text-center"
//             style={{
//               background:
//                 "radial-gradient(ellipse at center, rgba(0, 95, 94, 0.45) 0%, rgba(3, 74, 69, 0.25) 35%, rgba(255,255,255,1) 70%)",
//             }}
//           > */}
//           <div
//   className="relative w-full pt-10 pb-16 text-center overflow-hidden rounded-t-3xl"
//   style={{
//     background:
//       "radial-gradient(ellipse at top, rgba(180, 232, 231, 0.9) 0%, rgba(215, 245, 244, 0.7) 35%, rgba(255,255,255,1) 70%)",
//   }}
// >
//             <h2 className="text-xl md:text-2xl font-medium font-gotham text-[#1D1D1D]">
//               Your Promotion Journey
//             </h2>

//             {/* Waves */}
//             <div className="absolute left-6 top-20 opacity-40">
//               <svg width="36" height="8" viewBox="0 0 36 8" fill="none">
//                 <path
//                   d="M0 4C4 0 8 8 12 4C16 0 20 8 24 4C28 0 32 8 36 4"
//                   stroke="#E4E4E4"
//                   strokeWidth="1.5"
//                   fill="none"
//                 />
//               </svg>
//             </div>

//             <div className="absolute right-6 top-20 opacity-40">
//               <svg width="36" height="8" viewBox="0 0 36 8" fill="none">
//                 <path
//                   d="M0 4C4 0 8 8 12 4C16 0 20 8 24 4C28 0 32 8 36 4"
//                   stroke="#E4E4E4"
//                   strokeWidth="1.5"
//                   fill="none"
//                 />
//               </svg>
//             </div>

//             {/* Avatar with Profile Image */}
//             <div className="mt-6 flex justify-center">
//               <div className="w-20 h-20 md:h-[113.03515625px] md:w-[113.03515625px] rounded-full bg-white flex items-center justify-center shadow">
//                 <div className="w-24 h-24 md:h-[97.1584243774414px] md:w-[97.1584243774414px] rounded-full bg-[#F8F8F8] flex items-center justify-center text-xs text-[#1D1D1D] overflow-hidden">
//                   {profileImage ? (
//                     <img
//                       src={profileImage}
//                       alt="Profile"
//                       className="w-full h-full object-cover rounded-full"
//                     />
//                   ) : (
//                     <svg
//                       className="w-8 h-8 text-gray-400"
//                       fill="currentColor"
//                       viewBox="0 0 24 24"
//                     >
//                       <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
//                     </svg>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Inner Card */}
//           <div className="relative -mt-10 mx-4 md:mx-6 bg-white rounded-2xl border border-[#E3E3E3] overflow-hidden">
//             {/* Track Header */}
//             <div className="bg-[#208887] py-3 text-center" onClick={() => navigate("/learner/learning")}>
//               <p className="text-sm font-semibold font-gotham tracking-widest text-white underline">
//                 {/* CAREER PROGRESSION TRACK */}
//                 YOUR LEARNING PATH
//               </p>
//             </div>

//             {/* Modules */}
//             <div className="px-6 pt-6">
//               {loading ? (
//                 <div className="flex justify-center items-center py-4">
//                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
//                 </div>
//               ) : (
          
//                 <div className="flex justify-between">
//                   {courseProgress?.modules?.map((module, index) => {
//                     const allSubsCompleted = module.subsections?.every(
//                       (sub) => sub.completed || sub.status === "completed"
//                     );
//                     const someSubsCompleted = module.subsections?.some(
//                       (sub) => sub.completed || sub.status === "completed"
//                     );

//                     const isCompleted = allSubsCompleted;
//                     const isInProgress = !allSubsCompleted && someSubsCompleted;
//                     const isPending = !someSubsCompleted;

//                     let leaf;
//                     if (isCompleted) leaf = greenLeaf;
//                     else if (isInProgress) leaf = yellowLeaf;
//                     else leaf = silverLeaf;

//                     return (
//                       <div key={module.id} className="flex flex-col items-center">
//                         <div className="relative w-12 h-12">
//                           <img
//                             src={leaf}
//                             alt="module-status"
//                             className="w-full h-full object-contain"
//                           />
//                           <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-[#1D1D1D] bottom-2.5">
//                             M{index + 1}
//                           </span>
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>
//               )}
//             </div>

//             {/* Stats */}
//             <div className="grid grid-cols-3 text-center px-6 py-8 divide-x divide-gray-200 font-gotham">
//               <div>
//                 <p className="text-4xl font-medium text-[#323232]">
//                   {loading ? (
//                     <span className="inline-block w-12 h-10 bg-gray-200 rounded animate-pulse"></span>
//                   ) : (
//                     String(moduleStats.completed).padStart(2, "0")
//                   )}
//                 </p>
//                 <p className="mt-2 text-sm text-[#000000] font-gotham font-normal">
//                   modules completed
//                 </p>
//               </div>

//               <div>
//                 <p className="text-4xl font-medium text-[#323232]">
//                   {loading ? (
//                     <span className="inline-block w-12 h-10 bg-gray-200 rounded animate-pulse"></span>
//                   ) : (
//                     String(moduleStats.inProgress).padStart(2, "0")
//                   )}
//                 </p>
//                 <p className="mt-2 text-sm text-[#000000] font-gotham font-normal">
//                   module in-progress
//                 </p>
//               </div>

//               <div>
//                 <p className="text-4xl font-medium text-[#323232]">
//                   {loading ? (
//                     <span className="inline-block w-12 h-10 bg-gray-200 rounded animate-pulse"></span>
//                   ) : (
//                     String(moduleStats.pending).padStart(2, "0")
//                   )}
//                 </p>
//                 <p className="mt-2 text-sm text-[#000000] font-gotham font-normal">
//                   modules pending
//                 </p>
//               </div>
//             </div>
//           </div>

//           {/* CTA */}
//           <button
//             onClick={() => navigate("/learner/journey")}
//             className="mt-6 w-full bg-black text-white py-5 px-6 flex items-center justify-between text-base font-gotham md:text-lg font-medium hover:bg-gray-800 transition-colors"
//           >
//             <span>See Your Journey</span>
//             <span className="text-xl">
//               <FaArrowRight className="text-white" />
//             </span>
//           </button>
//         </div>

        

//         {/* Right Column */}
//         <div className="flex flex-col gap-6">
//           {/* Growth Partners */}
//           {/* <div className="rounded-2xl border border-[#E6E6E6] bg-white p-6 shadow-sm">
//             <div className="flex items-center justify-between mb-5">
//               <h3 className="text-lg md:text-xl font-medium text-[#1D1D1D]">
//                 Your Growth Partners
//               </h3>
//               <div className="flex-1 ml-4 h-px bg-[#E6E6E6]" />
//             </div>

//             {supportError && (
//               <p className="text-xs text-red-600 mb-4">{supportError}</p>
//             )}

//             <div className="flex flex-col sm:flex-row gap-10">
//               {[
//                 {
//                   key: "coach",
//                   name: supportLoading ? "Loading..." : coachName,
//                   role: "CAREER COACH",
//                 },
//                 {
//                   key: "ap",
//                   name: supportLoading ? "Loading..." : apName,
//                   role: "ACCOUNTABILITY PARTNER",
//                 },
//               ].map((p, i) => (
//                 <div key={p.key || i} className="flex items-center gap-4">
//                   <div className="w-14 h-14 rounded-full bg-[#DFF1EF] flex items-center justify-center overflow-hidden">
//                     <span className="text-sm text-gray-700 font-medium">
//                       {p.name?.[0]?.toUpperCase() || "?"}
//                     </span>
//                   </div>

//                   <div className="leading-tight">
//                     <div className="flex gap-1">
//                       <p className="font-medium font-gotham text-xl md:text-lg text-[#1C1C1C]">
//                         {p.name}
//                       </p>
//                       <span className="">
//                         <IoMdChatboxes className="text-[#54B0AF] text-xl mt-1" />
//                       </span>
//                     </div>
//                     <p className="text-xs md:text-[10px] text-[#AAAAAA] font-medium font-gotham uppercase tracking-wide">
//                       {p.role}
//                     </p>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div> */}
//           <div className="rounded-2xl border border-[#E6E6E6] bg-white p-6 shadow-sm">
//           <div className="flex items-center justify-between mb-5">
//             <h3 className="text-lg md:text-xl font-medium text-[#1D1D1D]">
//               Your Growth Partners
//             </h3>
//             <div className="flex-1 ml-4 h-px bg-[#E6E6E6]" />
//           </div>

//             {supportError && (
//               <p className="text-xs text-red-600 mb-4">{supportError}</p>
//             )}

//             {/* Equal width container */}
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
//               {[
//                 {
//                   key: "coach",
//                   name: supportLoading ? "Loading..." : coachName,
//                   role: "CAREER COACH",
//                 },
//                 {
//                   key: "ap",
//                   name: supportLoading ? "Loading..." : apName,
//                   role: "ACCOUNTABILITY PARTNER",
//                 },
//               ].map((p, i) => (
//                 <div
//                   key={p.key || i}
//                   className="flex items-center gap-4 w-full"
//                 >
//                   {/* Avatar */}
//                   <div className="w-14 h-14 rounded-full bg-[#DFF1EF] flex items-center justify-center overflow-hidden flex-shrink-0">
//                     <span className="text-sm text-gray-700 font-medium">
//                       {p.name?.[0]?.toUpperCase() || "?"}
//                     </span>
//                   </div>

//                   {/* Text */}
//                   <div className="leading-tight flex-1">
//                     <div className="flex items-center gap-1">
//                       <p className="font-medium font-gotham text-lg md:text-xl text-[#1C1C1C] truncate">
//                         {/* {p.name} */}
//                         {p.name && p.name.length > 13
//                           ? `${p.name.slice(0, 13)}...`
//                           : p.name}
//                       </p>
//                       {/* <IoMdChatboxes className="text-[#54B0AF] text-xl mt-[2px]" /> */}
//                     </div>

//                     <p className="text-xs md:text-[10px] text-[#AAAAAA] font-medium font-gotham uppercase tracking-wide">
//                       {p.role}
//                     </p>
//                   </div>
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* Daily Affirmation */}
//           <div className="relative rounded-2xl min-h-[360px] border border-[#E6E6E6] bg-white p-8 shadow-sm overflow-hidden">
//             {/* Header */}
//             <div className="flex items-center justify-between mb-10 relative z-10">
//               <h3 className="text-lg md:text-xl font-medium font-gotham text-[#1D1D1D]">
//                 Your Daily Affirmation
//               </h3>
//               <div className="flex-1 ml-4 h-px bg-[#E6E6E6]" />
//             </div>

//             {/* Content */}
//             <div className="relative z-10 flex flex-col items-center gap-6 text-center">
//               <div className="text-5xl leading-none text-gray-700">"</div>

//               <p className="text-xl sm:text-2xl  text-[#1D1D1D] font-gotham font-[300] leading-relaxed max-w-md">
//                 {dailyAffirmation.text}
//               </p>

//               {/* <p className="mt-4 text-sm italic text-[#1D1D1D] font-gotham font-normal absolute -bottom-3">
//                 {dailyAffirmation.source}
//               </p> */}
//             </div>
//               <p className="text-sm italic text-center w-full text-[#1D1D1D] font-gotham font-normal absolute bottom-5 flex justify-center">
//                 {dailyAffirmation.source}
//               </p>

//             {/* Bottom teal circular glow */}
//             <div
//               className="absolute left-1/2 -translate-x-1/2 bottom-[-45%]
//                         w-[65%] aspect-square rounded-full blur-[3px]"
//               style={{
//                 background:
//                   "radial-gradient(circle, rgba(28,239,204,0.26) 0%, rgba(12,184,181,0.16) 40%, rgba(49,236,208,0) 68%)",
//               }}
//             />
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default DashboardWelcomeSection;
